// tier lists: rows of ranked pictures, S at the top. each person keeps their own, and a list can be shared as a link
// anyone can open. the server and the app clean a list with the same code, so what is saved is always well formed

export const TIER_COLORS = [
  "#ff7f7f", "#ffbf7f", "#ffdf7f", "#bfff7f", "#7fff9f", "#7fffff", "#7fbfff", "#7f7fff", "#bf7fff", "#ff7fe0", "#c9c9c9", "#6b6f7b"
] as const;

export const DEFAULT_TIERS: Array<[string, string]> = [
  ["S", "#ff7f7f"],
  ["A", "#ffbf7f"],
  ["B", "#ffdf7f"],
  ["C", "#bfff7f"],
  ["D", "#7fbfff"],
  ["F", "#bf7fff"]
];

export const MAX_TIER_ITEMS = 150;
export const MAX_TIERS = 12;
export const MAX_TIER_LISTS = 100;
export const TIER_LABEL_MAX = 40;
export const TIER_TITLE_MAX = 80;

export const TIER_FILE = /^[a-f0-9]{12,40}\.(jpg|webp|png)$/;
const HEX = /^#[0-9a-f]{6}$/i;
const ID = /^[a-z0-9]{4,24}$/i;

export interface TierItem {
  id: string;
  // the picture in this person's tier list folder
  file: string;
  label: string;
  // added on the way out
  url?: string;
}

export interface Tier {
  id: string;
  label: string;
  color: string;
  // item ids, left to right
  items: string[];
}

export interface TierList {
  id: string;
  title: string;
  tiers: Tier[];
  // pictures not ranked yet
  pool: string[];
  items: Record<string, TierItem>;
  createdAt: string;
  updatedAt: string;
  // the token in the share link, empty while the list is private
  share: string;
}

export interface TierListSummary {
  id: string;
  title: string;
  updatedAt: string;
  count: number;
  ranked: number;
  shared: boolean;
  // up to four pictures from the top tiers
  covers: string[];
}

export interface TierImageDraft {
  name: string;
  data: string;
}

function text(value: unknown, max: number, fallback = ""): string {
  const clean = String(value ?? "").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, max);
  return clean || fallback;
}

function cleanId(value: unknown, fallback: string): string {
  const id = String(value ?? "");
  return ID.test(id) ? id : fallback;
}

export function newTierId(): string {
  return Array.from({ length: 10 }, () => "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 36)]).join("");
}

export function freshTierList(id: string, title: string, now: string): TierList {
  return {
    id,
    title,
    tiers: DEFAULT_TIERS.map(([label, color]) => ({ id: newTierId(), label, color, items: [] })),
    pool: [],
    items: {},
    createdAt: now,
    updatedAt: now,
    share: ""
  };
}

// whatever came in, as a list the rest of the code can trust. an item must be a known picture, appear once and sit
// in one place, anything left over goes back to the pool so nothing the person added gets lost
export function cleanTierList(raw: unknown, now: string, keep?: Partial<TierList>): TierList {
  const given = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const items: Record<string, TierItem> = {};
  const rawItems = given.items && typeof given.items === "object" ? given.items as Record<string, Record<string, unknown>> : {};
  for (const [key, item] of Object.entries(rawItems)) {
    if (Object.keys(items).length >= MAX_TIER_ITEMS) break;
    const id = cleanId(item?.id ?? key, "");
    const file = String(item?.file ?? "");
    if (!id || !TIER_FILE.test(file)) continue;
    items[id] = { id, file, label: text(item?.label, TIER_LABEL_MAX) };
  }
  const placed = new Set<string>();
  const take = (value: unknown): string[] => {
    const out: string[] = [];
    if (!Array.isArray(value)) return out;
    for (const entry of value) {
      const id = String(entry);
      if (items[id] && !placed.has(id)) {
        placed.add(id);
        out.push(id);
      }
    }
    return out;
  };
  const seenTiers = new Set<string>();
  const tiers: Tier[] = (Array.isArray(given.tiers) ? given.tiers : []).slice(0, MAX_TIERS).map((tier) => {
    const source = (tier || {}) as Record<string, unknown>;
    let id = cleanId(source.id, newTierId());
    while (seenTiers.has(id)) id = newTierId();
    seenTiers.add(id);
    return { id, label: text(source.label, TIER_LABEL_MAX), color: HEX.test(String(source.color)) ? String(source.color).toLowerCase() : "#c9c9c9", items: take(source.items) };
  });
  const pool = take(given.pool);
  for (const id of Object.keys(items)) if (!placed.has(id)) pool.push(id);
  return {
    id: cleanId(given.id, keep?.id || newTierId()),
    title: text(given.title, TIER_TITLE_MAX, "tier list"),
    tiers,
    pool,
    items,
    createdAt: keep?.createdAt || String(given.createdAt || now),
    updatedAt: now,
    share: keep?.share ?? ""
  };
}

// dark text on a light cap, light text on a dark one
export function capInk(color: string): string {
  const value = parseInt(color.slice(1), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#10131b" : "#ffffff";
}
